import { Box, Paper, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CoveragePeriodsGrid from 'modules/opsManagerDashboard/components/CoveragePeriodsGrid.component';
import EaExpenseOverviewGrid from './components/EaExpenseOverviewGrid.component';
import ExceptionCommentsGrid from 'modules/dashboard/components/ExceptionCommentsGrid.component';
import ExpenseFollowUpGrid from 'modules/dashboard/components/ExpenseFollowUpGrid.component';
import ExpenseManagerDashboardFilters from './components/ExpenseManagerDashboardFilters.component';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo } from 'react';
import RecurringPayablesGrid from 'modules/dashboard/components/RecurringPayablesGrid.component';
import VoucherFollowUpGrid from 'modules/eaDashboard/components/VoucherFollowUpGrid.component';

const useStyles = makeStyles((theme) => ({
  dashboardContainer: {
    padding: theme.spacing(3),
  },
  filtersContainer: {
    marginTop: theme.spacing(2),
  },
  sectionPaper: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),
  },
}));

const ExpenseManagerDashboardComponent = ({
  clients,
  filters,
  onFilterChange,
  isLoading,
  followUpExpenses,
  followUpVouchers,
  recurringPayables,
  coveragePeriods,
  exceptionComments,
  expenseAnalystSummaries,
  isLoadingDashboard,
}) => {
  const classes = useStyles();

  // Employee/File options come from the rows already loaded, so the list is
  // always scoped to files that actually have work on the dashboard
  const fileOptions = useMemo(() => {
    const allRows = [...followUpExpenses, ...followUpVouchers, ...recurringPayables, ...exceptionComments];
    const optionsByFileId = new Map();

    allRows.forEach((row) => {
      if (row.fileId && !optionsByFileId.has(row.fileId)) {
        optionsByFileId.set(row.fileId, {
          fileId: row.fileId,
          authId: row.authId || '',
          eeName: row.eeName || '',
        });
      }
    });

    return [...optionsByFileId.values()].sort((firstFile, secondFile) =>
      `${firstFile.authId}`.localeCompare(`${secondFile.authId}`),
    );
  }, [followUpExpenses, followUpVouchers, recurringPayables, exceptionComments]);

  // Expense analyst options are drawn from the loaded rows as well, alphabetized
  const expenseAnalystOptions = useMemo(() => {
    const allRows = [...followUpExpenses, ...followUpVouchers, ...recurringPayables, ...exceptionComments];
    const uniqueAnalysts = [...new Set(allRows.map((row) => row.expenseAnalystName).filter(Boolean))];
    return uniqueAnalysts.sort((firstName, secondName) => firstName.localeCompare(secondName));
  }, [followUpExpenses, followUpVouchers, recurringPayables, exceptionComments]);

  // The dashboard rows carry clientName rather than a client id, so the selected
  // client is matched by name — the same approach the EA dashboard uses
  const selectedClientNames = useMemo(() => {
    if (!filters.clientId) return null;

    const selectedClient = clients.find((client) => client.clientRecordId === filters.clientId);
    if (!selectedClient) return null;

    return [selectedClient.shortName, selectedClient.legalName]
      .filter(Boolean)
      .map((name) => name.toLowerCase());
  }, [clients, filters.clientId]);

  const applyFilters = useCallback((rows) => {
    let filtered = rows;

    if (selectedClientNames) {
      filtered = filtered.filter((row) => selectedClientNames.includes((row.clientName || '').toLowerCase()));
    }

    if (filters.fileId) {
      filtered = filtered.filter((row) => row.fileId === filters.fileId);
    }

    if (filters.expenseAnalystName) {
      filtered = filtered.filter((row) => row.expenseAnalystName === filters.expenseAnalystName);
    }

    return filtered;
  }, [selectedClientNames, filters.fileId, filters.expenseAnalystName]);

  const filteredFollowUpExpenses = useMemo(() => applyFilters(followUpExpenses), [applyFilters, followUpExpenses]);
  const filteredFollowUpVouchers = useMemo(() => applyFilters(followUpVouchers), [applyFilters, followUpVouchers]);
  const filteredRecurringPayables = useMemo(() => applyFilters(recurringPayables), [applyFilters, recurringPayables]);
  const filteredExceptionComments = useMemo(() => applyFilters(exceptionComments), [applyFilters, exceptionComments]);

  // Coverage periods carry no client or file information, so of the dashboard
  // filters only the expense analyst (the covered user) applies to them
  const filteredCoveragePeriods = useMemo(() => {
    if (!filters.expenseAnalystName) return coveragePeriods;
    return coveragePeriods.filter((period) => period.rcName === filters.expenseAnalystName);
  }, [coveragePeriods, filters.expenseAnalystName]);

  // The overview summaries are pre-aggregated per analyst, so likewise only the
  // expense analyst filter applies — grid-level filters never affect this table
  const filteredExpenseAnalystSummaries = useMemo(() => {
    if (!filters.expenseAnalystName) return expenseAnalystSummaries;
    return expenseAnalystSummaries.filter((summary) => summary.expenseAnalystName === filters.expenseAnalystName);
  }, [expenseAnalystSummaries, filters.expenseAnalystName]);

  return (
    <Box className={classes.dashboardContainer}>
      <Typography variant="h4" gutterBottom>
        Expense Manager Dashboard
      </Typography>

      {/* Filters for all tables on the dashboard */}
      <Box className={classes.filtersContainer}>
        <ExpenseManagerDashboardFilters
          filters={filters}
          onFilterChange={onFilterChange}
          clients={clients}
          files={fileOptions}
          expenseAnalysts={expenseAnalystOptions}
          isLoading={isLoading}
        />
      </Box>

      {/* EA Expense Overview Section */}
      <Paper className={classes.sectionPaper}>
        <Typography variant="h5" gutterBottom>
          EA Expense Overview
        </Typography>

        <EaExpenseOverviewGrid
          summaries={filteredExpenseAnalystSummaries}
          isLoading={isLoadingDashboard}
        />
      </Paper>

      {/* Expense Follow-up Section */}
      <Paper className={classes.sectionPaper}>
        <Typography variant="h5" gutterBottom>
          Expense Follow-up
        </Typography>

        <ExpenseFollowUpGrid
          expenses={filteredFollowUpExpenses}
          isLoading={isLoadingDashboard}
          clients={clients}
          showExpenseAnalyst
          showLineItemStatus
          defaultDaysRange="lt7"
        />
      </Paper>

      {/* Voucher Follow-up Section */}
      <Paper className={classes.sectionPaper}>
        <Typography variant="h5" gutterBottom>
          Voucher Follow-up
        </Typography>

        <VoucherFollowUpGrid
          vouchers={filteredFollowUpVouchers}
          isLoading={isLoadingDashboard}
          showExpenseAnalyst
        />
      </Paper>

      {/* Recurring Payables Section */}
      <Paper className={classes.sectionPaper}>
        <Typography variant="h5" gutterBottom>
          Active Recurring Payables
        </Typography>

        <RecurringPayablesGrid
          recurringPayables={filteredRecurringPayables}
          isLoading={isLoadingDashboard}
          showExpenseAnalyst
        />
      </Paper>

      {/* Exceptions Section */}
      <Paper className={classes.sectionPaper}>
        <Typography variant="h5" gutterBottom>
          Exceptions
        </Typography>

        <ExceptionCommentsGrid
          exceptions={filteredExceptionComments}
          isLoading={isLoadingDashboard}
          clients={clients}
          showExpenseAnalyst
        />
      </Paper>

      {/* Coverage Periods Section */}
      <Paper className={classes.sectionPaper}>
        <Typography variant="h5" gutterBottom>
          Coverage Periods
        </Typography>

        <CoveragePeriodsGrid
          periods={filteredCoveragePeriods}
          isLoading={isLoadingDashboard}
          rcColumnHeader="Expense Analyst"
        />
      </Paper>
    </Box>
  );
};

ExpenseManagerDashboardComponent.propTypes = {
  clients: PropTypes.array,
  filters: PropTypes.object,
  onFilterChange: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  followUpExpenses: PropTypes.array,
  followUpVouchers: PropTypes.array,
  recurringPayables: PropTypes.array,
  coveragePeriods: PropTypes.array,
  exceptionComments: PropTypes.array,
  expenseAnalystSummaries: PropTypes.array,
  isLoadingDashboard: PropTypes.bool,
};

ExpenseManagerDashboardComponent.defaultProps = {
  clients: [],
  filters: {},
  isLoading: false,
  followUpExpenses: [],
  followUpVouchers: [],
  recurringPayables: [],
  coveragePeriods: [],
  exceptionComments: [],
  expenseAnalystSummaries: [],
  isLoadingDashboard: false,
};

export default ExpenseManagerDashboardComponent;
